import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import {
  Plus, ChevronLeft, ChevronRight, CalendarRange, LayoutGrid, X, Layers,
  User, FolderKanban, ArrowUpRight,
} from 'lucide-react'
import type { ContentPlan } from '@/lib/types'
import { useToast } from '@/context/ToastContext'
import { useContentPlans } from '@/lib/useContentPlans'
import { ContentPlanModal } from '@/components/ContentPlanModal'
import {
  CONTENT_STATUS_CONFIG, CONTENT_STATUS_ORDER, buildMonthGrid, parseDateKey, toDateKey,
  MONTHS_ID, DAYS_ID_SHORT, channelTint, formatPlanDate, dayNameLong,
} from '@/lib/contentPlan'
import { hasCrossDeptView } from '@/lib/roles'
import { deptColor, DEPARTMENTS } from '@/lib/departments'

function ViewToggle({ active }: { active: 'content' | 'calendar' }) {
  const navigate = useNavigate()
  const Btn = ({ id, label, Icon, href }: { id: 'content' | 'calendar'; label: string; Icon: typeof LayoutGrid; href: string }) => (
    <button onClick={() => navigate(href)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-body font-medium transition-all ${active === id ? 'bg-white text-[#111110] shadow-sm' : 'text-[#8A8A85] hover:text-[#1A1916]'}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  )
  return (
    <div className="flex items-center gap-1 bg-[#F4F3F0] rounded-xl p-1 flex-shrink-0">
      <Btn id="content" label="Content" Icon={LayoutGrid} href="/content-plan" />
      <Btn id="calendar" label="Kalender" Icon={CalendarRange} href="/calendar-plan" />
    </div>
  )
}

// ── Detail panel (klik pill) — read-only ringkas + aksi ─────
function DetailPanel({ plan, onClose, onEdit, onJump }: {
  plan: ContentPlan
  onClose: () => void
  onEdit: () => void
  onJump: () => void
}) {
  const dc = deptColor(plan.department)
  const c = CONTENT_STATUS_CONFIG[plan.status]
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-[480px] max-h-[88vh] flex flex-col bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up"
        style={{ borderTopWidth: '4px', borderTopColor: plan.color }}>
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-body font-semibold ${c.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> {c.label}
            </span>
            {plan.department && (
              <span className="text-[9px] font-body font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border"
                style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}>{plan.department}</span>
            )}
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-1 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-[11px] text-[#AEADA8] font-body mb-1">{dayNameLong(plan.planDate)}, {formatPlanDate(plan.planDate)}</p>
          <h2 className="font-display text-xl font-bold text-[#1A1916] leading-snug mb-4">{plan.title}</h2>

          {plan.contentBank.length > 0 && (
            <div className="mb-4">
              <p className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-2"><Layers className="w-2.5 h-2.5" /> Content Bank</p>
              <div className="flex flex-wrap gap-1.5">
                {plan.contentBank.map((b, i) => <span key={i} className="px-2 py-0.5 rounded-md bg-[#F4F3F0] text-[11px] font-body text-[#3A3A36]">{b}</span>)}
              </div>
            </div>
          )}

          {plan.funnel.length > 0 && (
            <div className="mb-4">
              <p className="text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-2">Funnel</p>
              <div className="space-y-2">
                {plan.funnel.map((f, i) => {
                  const t = channelTint(f.channel)
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wide border flex-shrink-0"
                        style={{ backgroundColor: t.bg, borderColor: t.border, color: t.text }}>{f.channel}</span>
                      <span className="text-[12px] text-[#6B6860] font-body leading-relaxed">{f.items.join(' · ') || '—'}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {plan.notes && <p className="text-[13px] text-[#3A3A36] font-body leading-relaxed whitespace-pre-wrap mb-4">{plan.notes}</p>}

          <div className="flex items-center gap-4 pt-3 border-t border-[#F4F3F0] text-[11px] text-[#8A8A85] font-body">
            {plan.pic.length > 0 && <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-[#C0BDB6]" /> {plan.pic.join(', ')}</span>}
            {plan.projectName && <span className="inline-flex items-center gap-1 text-[#6366F1]"><FolderKanban className="w-3 h-3" /> {plan.projectName}</span>}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#F2F1EC] flex items-center justify-between flex-shrink-0">
          <button onClick={onJump} className="flex items-center gap-1 text-[12px] font-body text-[#6366F1] hover:text-[#4338CA] transition-colors">
            Buka di Content Plan <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={onEdit} className="px-5 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all">
            Edit
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Day list (klik "+N lainnya") — semua konten di satu hari ─
function DayListPanel({ dateKey, items, onClose, onPick, onAdd }: {
  dateKey: string
  items: ContentPlan[]
  onClose: () => void
  onPick: (plan: ContentPlan) => void
  onAdd: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-[420px] max-h-[80vh] flex flex-col bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div>
            <p className="text-[11px] text-[#AEADA8] font-body leading-tight">{dayNameLong(dateKey)}</p>
            <p className="font-display text-[15px] font-bold text-[#1A1916] leading-tight">{formatPlanDate(dateKey)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {items.map((p) => {
            const c = CONTENT_STATUS_CONFIG[p.status]
            return (
              <button key={p.id} onClick={() => onPick(p)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-[#EEEDE8] hover:border-[#C8C7BF] hover:bg-[#FAFAF8] text-left transition-all"
                style={{ borderLeftWidth: '3px', borderLeftColor: p.color }}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-body font-semibold text-[#1A1916] truncate">{p.title}</span>
                  <span className="block text-[10px] text-[#AEADA8] font-body">{c.label}{p.pic.length ? ` · ${p.pic.join(', ')}` : ''}</span>
                </span>
                <ArrowUpRight className="w-3.5 h-3.5 text-[#C0BDB6] flex-shrink-0" />
              </button>
            )
          })}
        </div>
        <div className="px-5 py-3 border-t border-[#F2F1EC] flex-shrink-0">
          <button onClick={onAdd}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-[#D6D5CF] text-[12px] font-body text-[#6B6860] hover:border-[#C8C7BF] hover:bg-[#FAFAF8] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Tambah konten di tanggal ini
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CalendarPlanPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { data: session } = useSession()
  const { toast } = useToast()
  const { plans, loading, createPlan, updatePlan, deletePlan } = useContentPlans()

  // Bulan awal di-set sekali saat mount; "hari ini" dihitung tiap render agar
  // tidak basi pada tab yang dibuka melewati tengah malam.
  const initialNow = useMemo(() => new Date(), [])
  const [year, setYear] = useState(initialNow.getFullYear())
  const [month, setMonth] = useState(initialNow.getMonth())
  const [selectedDept, setSelectedDept] = useState<string | null>(null)
  const [detail, setDetail] = useState<ContentPlan | null>(null)
  const [editing, setEditing] = useState<ContentPlan | null>(null)
  const [creating, setCreating] = useState<{ date?: string } | null>(null)
  const [dayList, setDayList] = useState<string | null>(null)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const holding = hasCrossDeptView(session?.user ?? {})
  const todayKey = toDateKey(new Date())

  // Deep-link dari Content Plan: ?focus=<id> → loncat ke bulannya, sorot & buka detail.
  const handledFocus = useRef(false)
  useEffect(() => {
    if (handledFocus.current || loading) return
    const focus = params.get('focus')
    const date = params.get('date')
    if (focus) {
      handledFocus.current = true
      const target = plans.find((p) => p.id === focus)
      if (target) {
        const d = parseDateKey(target.planDate)
        setYear(d.getFullYear()); setMonth(d.getMonth())
        setHighlightId(focus)
        setDetail(target)
        setTimeout(() => setHighlightId(null), 2800)
      } else {
        toast('Konten tidak ditemukan atau di luar akses kamu.', 'info')
      }
      navigate('/calendar-plan', { replace: true })
    } else if (date) {
      handledFocus.current = true
      const d = parseDateKey(date)
      setYear(d.getFullYear()); setMonth(d.getMonth())
      setCreating({ date })
      navigate('/calendar-plan', { replace: true })
    }
  }, [params, plans, loading, navigate, toast])

  const scopedPlans = useMemo(
    () => (holding && selectedDept ? plans.filter((p) => p.department === selectedDept) : plans),
    [plans, holding, selectedDept],
  )

  // Index per tanggal untuk lookup cepat di tiap cell.
  const byDate = useMemo(() => {
    const map = new Map<string, ContentPlan[]>()
    for (const p of scopedPlans) {
      const arr = map.get(p.planDate) ?? []
      arr.push(p)
      map.set(p.planDate, arr)
    }
    return map
  }, [scopedPlans])

  const grid = useMemo(() => buildMonthGrid(year, month, todayKey), [year, month, todayKey])
  const monthCount = useMemo(
    () => scopedPlans.filter((p) => { const d = parseDateKey(p.planDate); return d.getFullYear() === year && d.getMonth() === month }).length,
    [scopedPlans, year, month],
  )

  const deptOptions = useMemo(
    () => Array.from(new Set([...DEPARTMENTS, ...plans.map((p) => p.department).filter(Boolean) as string[]])),
    [plans],
  )

  const goMonth = (delta: number) => {
    const d = new Date(year, month + delta, 1)
    setYear(d.getFullYear()); setMonth(d.getMonth())
  }
  const goToday = () => { const d = new Date(); setYear(d.getFullYear()); setMonth(d.getMonth()) }
  const goContent = (plan: ContentPlan) => navigate(`/content-plan?focus=${plan.id}`)

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-4" style={{ height: '52px' }}>
          <span className="font-display text-[15px] font-bold text-[#1A1916] flex-shrink-0">Calendar Plan</span>
          <ViewToggle active="calendar" />

          {/* Month nav */}
          <div className="flex items-center gap-1.5">
            <button onClick={() => goMonth(-1)} className="p-1.5 rounded-lg text-[#6B6860] hover:bg-[#F4F3F0] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-display text-[14px] font-bold text-[#1A1916] min-w-[120px] text-center tabular-nums">{MONTHS_ID[month]} {year}</span>
            <button onClick={() => goMonth(1)} className="p-1.5 rounded-lg text-[#6B6860] hover:bg-[#F4F3F0] transition-colors"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={goToday} className="ml-1 px-2.5 py-1 rounded-lg border border-[#E8E7E3] text-[11px] font-body font-medium text-[#6B6860] hover:border-[#C8C7BF] transition-colors">Hari ini</button>
          </div>

          <div className="flex-1" />
          <span className="text-[11px] text-[#AEADA8] font-body hidden md:inline">{monthCount} konten bulan ini</span>

          {holding && (
            <select value={selectedDept ?? ''} onChange={(e) => setSelectedDept(e.target.value || null)}
              className="px-3 py-1.5 bg-white border border-[#E8E7E3] rounded-xl text-xs font-body text-[#6B6860] hover:border-[#C8C7BF] focus:outline-none focus:border-[#1A1916] cursor-pointer transition-colors flex-shrink-0">
              <option value="">Semua Departemen</option>
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <button onClick={() => setCreating({})}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all">
            <Plus className="w-3.5 h-3.5" /> Tambah Konten
          </button>
        </div>
      </header>

      <main className="px-6 py-5">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 flex-wrap">
          {CONTENT_STATUS_ORDER.map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-[11px] font-body text-[#6B6860]">
              <span className={`w-2 h-2 rounded-full ${CONTENT_STATUS_CONFIG[s].dot}`} /> {CONTENT_STATUS_CONFIG[s].label}
            </span>
          ))}
        </div>

        <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-[#E8E7E3]">
            {DAYS_ID_SHORT.map((d, i) => (
              <div key={d} className={`px-3 py-2.5 text-[11px] font-body font-bold uppercase tracking-wider text-center ${i >= 5 ? 'text-[#B45A5A] bg-[#FBEFEF]' : 'text-[#8A8A85]'}`}>{d}</div>
            ))}
          </div>

          {/* Days grid — re-animate saat ganti bulan */}
          <div key={`${year}-${month}`} className="grid grid-cols-7 animate-fade-in">
            {grid.map((cell, i) => {
              const items = byDate.get(cell.key) ?? []
              const shown = items.slice(0, 3)
              const extra = items.length - shown.length
              return (
                <div key={cell.key}
                  onClick={() => setCreating({ date: cell.key })}
                  className={`group/cell min-h-[116px] p-1.5 border-b border-r border-[#F0EFE9] cursor-pointer transition-colors
                    ${(i + 1) % 7 === 0 ? 'border-r-0' : ''}
                    ${!cell.inMonth ? 'bg-[#FAFAF8]' : cell.isWeekend ? 'bg-[#FDF6F6]' : 'bg-white'}
                    hover:bg-[#F8F7F3]`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`inline-flex items-center justify-center text-[11px] font-body font-semibold w-5 h-5 rounded-full
                      ${cell.isToday ? 'bg-[#1A1916] text-white' : cell.inMonth ? 'text-[#3A3A36]' : 'text-[#C8C7BF]'}`}>
                      {cell.date.getDate()}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); setCreating({ date: cell.key }) }}
                      className="opacity-0 group-hover/cell:opacity-100 p-0.5 rounded text-[#C0BDB6] hover:text-[#1A1916] hover:bg-[#F2F1EC] transition-all">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {shown.map((p) => {
                      const c = CONTENT_STATUS_CONFIG[p.status]
                      const hl = highlightId === p.id
                      return (
                        <button key={p.id}
                          onClick={(e) => { e.stopPropagation(); setDetail(p) }}
                          title={p.title}
                          className={`w-full flex items-center gap-1 pl-1.5 pr-1 py-1 rounded-md text-left transition-all hover:-translate-y-px hover:shadow-sm ${hl ? 'ring-2 ring-[#6366F1] ring-offset-1' : ''}`}
                          style={{ backgroundColor: `${p.color}14` }}>
                          <span className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                          <span className="text-[10.5px] font-body font-medium text-[#1A1916] truncate leading-tight">{p.title}</span>
                        </button>
                      )
                    })}
                    {extra > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); setDayList(cell.key) }}
                        className="w-full text-left px-1.5 text-[10px] font-body font-medium text-[#8A8A85] hover:text-[#6366F1] transition-colors">
                        +{extra} lainnya
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </main>

      {dayList && (
        <DayListPanel
          dateKey={dayList}
          items={byDate.get(dayList) ?? []}
          onClose={() => setDayList(null)}
          onPick={(p) => { setDayList(null); setDetail(p) }}
          onAdd={() => { const d = dayList; setDayList(null); setCreating({ date: d }) }}
        />
      )}

      {detail && (
        <DetailPanel plan={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null) }}
          onJump={() => goContent(detail)} />
      )}

      {(creating || editing) && (
        <ContentPlanModal
          plan={editing}
          defaultDate={creating?.date}
          onClose={() => { setEditing(null); setCreating(null) }}
          onCreate={createPlan}
          onUpdate={updatePlan}
          onDelete={deletePlan}
        />
      )}
    </div>
  )
}
